import { useState } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, fmtMoney, fmtPct, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ChartLineUp, ArrowSquareOut } from "@phosphor-icons/react";

const STRATEGY_LABEL = {
  ma_crossover: "MA Crossover",
  rsi: "RSI",
  macd: "MACD",
  bollinger: "Bollinger Bands",
};

export default function BacktestModal({ open, onOpenChange, config }) {
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [period, setPeriod] = useState("1y");

  const run = async (p = period) => {
    if (!config?.symbol) return;
    setBusy(true);
    setPeriod(p);
    try {
      const { data } = await api.post("/bots/backtest", {
        symbol: config.symbol,
        strategy: config.strategy,
        quantity: config.quantity,
        initial_cash: 10000,
        period: p,
        fast_period: config.fast_period,
        slow_period: config.slow_period,
        rsi_period: config.rsi_period,
        rsi_oversold: config.rsi_oversold,
        rsi_overbought: config.rsi_overbought,
        macd_fast: config.macd_fast,
        macd_slow: config.macd_slow,
        macd_signal: config.macd_signal,
        bb_period: config.bb_period,
        bb_std: config.bb_std,
      });
      setResult(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Backtest failed");
    } finally {
      setBusy(false);
    }
  };

  const equity = result?.equity_curve || [];
  const up = (result?.total_return_pct ?? 0) >= 0;
  const beatBH = result && result.total_return_pct > result.buy_and_hold_pct;

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setResult(null); }}>
      <DialogContent className="bg-zinc-900 border-zinc-800 rounded-sm max-w-3xl">
        <DialogHeader>
          <DialogTitle className="heading text-2xl tracking-tighter flex items-center gap-2">
            <ChartLineUp size={22} weight="regular" />
            Backtest · {config?.symbol} · {STRATEGY_LABEL[config?.strategy] || config?.strategy}
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">
            Simulates strategy on historical daily bars.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 border-b border-zinc-800 pb-3">
          <span className="mono text-[10px] text-zinc-500 uppercase tracking-widest mr-2">Period</span>
          {["3mo", "6mo", "1y", "2y", "5y"].map((p) => (
            <button
              key={p}
              data-testid={`bt-period-${p}`}
              onClick={() => run(p)}
              className={`px-3 py-1 mono text-[10px] uppercase tracking-widest rounded-sm border ${
                period === p && result ? "border-white bg-white text-black" : "border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-600"
              }`}
            >{p}</button>
          ))}
          {!result && (
            <Button
              onClick={() => run()}
              disabled={busy}
              data-testid="bt-run-btn"
              className="ml-auto bg-[#00C805] text-black hover:bg-[#00b004] rounded-sm h-8 mono text-[10px] uppercase tracking-widest"
            >
              {busy ? "Running…" : "Run backtest"}
            </Button>
          )}
        </div>

        {busy && !result && (
          <div className="py-12 text-center mono text-xs text-zinc-500">Simulating strategy…</div>
        )}

        {result && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <StatBlock label="Final equity" value={fmtMoney(result.final_equity)} tone={up ? "profit" : "loss"} />
              <StatBlock label="Return" value={fmtPct(result.total_return_pct)} tone={up ? "profit" : "loss"} />
              <StatBlock label="vs Buy & Hold" value={fmtPct(result.buy_and_hold_pct)} tone={beatBH ? "profit" : "loss"} sub={beatBH ? "STRATEGY WINS" : "B&H WINS"} />
              <StatBlock label="Max drawdown" value={`-${result.max_drawdown_pct}%`} tone="loss" />
              <StatBlock label="Trades" value={result.num_trades} sub={`${result.num_buys} BUY · ${result.num_sells} SELL`} />
              <StatBlock label="Win rate" value={fmtPct(result.win_rate)} tone={result.win_rate >= 50 ? "profit" : "loss"} />
              <StatBlock label="Realized P/L" value={fmtMoney(result.realized_pl, { showSign: true })} tone={result.realized_pl >= 0 ? "profit" : "loss"} />
              <StatBlock label="Initial" value={fmtMoney(result.initial_cash)} />
            </div>

            <div className="border border-zinc-800 rounded-sm p-2 bg-zinc-950">
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={equity} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="btfill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={up ? "#00C805" : "#FF3B30"} stopOpacity={0.3} />
                      <stop offset="100%" stopColor={up ? "#00C805" : "#FF3B30"} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="t" tick={{ fontFamily: "JetBrains Mono", fontSize: 10, fill: "#71717a" }}
                    axisLine={{ stroke: "#27272a" }} tickLine={false} minTickGap={40}
                    tickFormatter={(v) => { const d = new Date(v); return `${d.getMonth() + 1}/${d.getFullYear().toString().slice(-2)}`; }} />
                  <YAxis domain={["auto", "auto"]} tick={{ fontFamily: "JetBrains Mono", fontSize: 10, fill: "#71717a" }}
                    axisLine={{ stroke: "#27272a" }} tickLine={false} orientation="right" width={60}
                    tickFormatter={(v) => `$${(v / 1000).toFixed(1)}k`} />
                  <Tooltip contentStyle={{ backgroundColor: "#18181b", border: "1px solid #27272a", borderRadius: 2, fontFamily: "JetBrains Mono", fontSize: 11 }}
                    formatter={(v) => [fmtMoney(v), "Equity"]}
                    labelFormatter={(v) => new Date(v).toLocaleDateString()} />
                  <ReferenceLine y={result.initial_cash} stroke="#3f3f46" strokeDasharray="2 3" />
                  <Area type="monotone" dataKey="equity" stroke={up ? "#00C805" : "#FF3B30"} strokeWidth={1.5} fill="url(#btfill)" dot={false} isAnimationActive={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="max-h-40 overflow-y-auto border border-zinc-800 rounded-sm">
              <table className="w-full text-xs mono">
                <thead className="text-[10px] uppercase tracking-widest text-zinc-500 sticky top-0 bg-zinc-900">
                  <tr>
                    <th className="text-left px-3 py-2">Date</th>
                    <th className="text-left px-3 py-2">Side</th>
                    <th className="text-right px-3 py-2">Qty</th>
                    <th className="text-right px-3 py-2">Price</th>
                    <th className="text-right px-3 py-2">Realized P/L</th>
                  </tr>
                </thead>
                <tbody>
                  {result.trades.slice(-15).reverse().map((t, i) => (
                    <tr key={i} className="border-t border-zinc-800/50">
                      <td className="px-3 py-1.5 text-zinc-400">{t.t.slice(0, 10)}</td>
                      <td className={`px-3 py-1.5 uppercase font-bold ${t.side === "buy" ? "profit" : "loss"}`}>{t.side}</td>
                      <td className="text-right px-3 py-1.5">{t.quantity}</td>
                      <td className="text-right px-3 py-1.5">{fmtMoney(t.price)}</td>
                      <td className={`text-right px-3 py-1.5 ${t.realized_pl > 0 ? "profit" : t.realized_pl < 0 ? "loss" : "text-zinc-500"}`}>
                        {t.side === "sell" ? fmtMoney(t.realized_pl, { showSign: true }) : "—"}
                      </td>
                    </tr>
                  ))}
                  {result.trades.length === 0 && (
                    <tr><td colSpan={5} className="px-3 py-6 text-center text-zinc-600">No signals fired in this period.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function StatBlock({ label, value, sub, tone = "default" }) {
  const tc = tone === "profit" ? "profit" : tone === "loss" ? "loss" : "text-white";
  return (
    <div className="border border-zinc-800 rounded-sm p-3 bg-zinc-950">
      <div className="mono text-[9px] text-zinc-500 uppercase tracking-widest">{label}</div>
      <div className={`mono text-lg font-bold mt-1 ${tc}`}>{value}</div>
      {sub && <div className="mono text-[9px] text-zinc-500 mt-1 uppercase">{sub}</div>}
    </div>
  );
}
